import { secondsToTimecodeFrame, timecodeFrameToSeconds } from 'weave-timeline';
import { useTranslation } from 'react-i18next';
import { clampValue } from '@/utils/numbers';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { StepBackFrameIcon, StepForwardFrameIcon } from '@/UI/Icons';
import { I18N_KEYS } from '@/language/keys';

interface FrameStepButtonProps {
  direction: 'back' | 'forward';
  currentTime: number;
  duration: number;
  fps?: number;
  paused: boolean;
  onTogglePlay: () => void;
  onSetTime: (time: number, ongoing: boolean) => void;
  width?: number;
  height?: number;
  btnW?: number;
  btnH?: number;
}

export function FrameStepButton({
  direction,
  currentTime,
  duration,
  fps,
  paused,
  onTogglePlay,
  onSetTime,
  width = 24,
  height = 24,
  btnW = 24,
  btnH = 24,
}: FrameStepButtonProps) {
  const { t } = useTranslation();
  const disabled = fps === undefined;

  const handleClick = () => {
    if (fps === undefined) return;
    if (!paused) onTogglePlay();
    const currentFrame = secondsToTimecodeFrame(currentTime, fps);
    const nextFrame = currentFrame + (direction === 'forward' ? 1 : -1);
    const nextTime = timecodeFrameToSeconds(nextFrame, fps);
    onSetTime(clampValue(nextTime, 0, duration), false);
  };

  const Icon = direction === 'forward' ? StepForwardFrameIcon : StepBackFrameIcon;

  return (
    <AppIconButton
      aria-label={t(
        direction === 'forward' ? I18N_KEYS.GENERAL.ARIA.NEXT_FRAME : I18N_KEYS.GENERAL.ARIA.PREVIOUS_FRAME,
      )}
      width={width}
      height={height}
      mode="on-dark"
      onClick={handleClick}
      disabled={disabled}
    >
      <Icon width={btnW} height={btnH} />
    </AppIconButton>
  );
}
